import { handleActions } from 'redux-actions';
import moment from 'moment';

import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import pick from 'lodash/pick';
import map from 'lodash/map';

import createCalendar, { getDateRangeWithTimeZoneFromCalendar } from '../shared/calendar';
import createFilters, { getFiltersObject } from '../shared/filters';
import http from '../shared/http';

import endpoints from '../../config/endpoints';

import { regions } from '../../../../../enums/regions';

export const namespace = 'managersAffiliatesActivity';

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().startOf('year').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'managers',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS + '?role=manager'
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'countries',
			title: 'Countries',
			placeholder: 'Add countries',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		}
	]
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

// initial state
const initialState = {
	isLoading: true,
	isError: false,
	data: null,
	filters: filters.initialState,
	calendar: calendar.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			request(getRequestQuery(getState), FETCH_DATA_SUCCESS)(dispatch);
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	},
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(actions.own.fetchData());
	},
	filters: filters.actions,
	calendar: calendar.actions
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const months = getMonths(getDateRangeWithTimeZoneFromCalendar(state.calendar));

			return {
				...state,
				data: {
					rows: formatData(action.payload.data, months),
					months
				},
				isLoading: false,
				isError: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			isLoading: true
		}),
		[CLEAR]: (state, action) => initialState
	},
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state)
};

function getRequestQuery(getState) {
	const state = getState()[namespace];

	const range = getDateRangeWithTimeZoneFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		dates: [range.start, range.end],
		filters,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: 'v2/managers-affiliates-activity',
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}

function getMonths(range) {
	const start = moment(range.start).startOf('month');
	const end = moment(range.end).startOf('month');

	const n = end.diff(start, 'month');

	return Array.from({ length: n + 1 }, (_, i) => start.clone().add(i, 'month'));
}

function formatData(rows, months) {
	const results = rows.map(({ data, ...row }) => ({
		...row,
		months: months.map((month) => {
			const info = data.find((info) => month.isSame(info.month, 'month'));

			return info ? pick(info, ['profit', 'count', 'valid', 'approved', 'generic']) : {
				profit: null,
				count: 0,
				valid: 0,
				approved: 0,
				generic: 0
			};
		})
	}));

	return map(groupBy(results, 'manager'), (values, manager) => {
		const rows = map(groupBy(values, 'rank'), (values, rank) => {
			const rows = values.map((v) => ({
				name: v.affiliate,
				id: v.affiliate_id,
				months: sumInfo([v.months]),
				total: {
					amount: v.months.filter((m) => m.valid > 0).length ? 1 : 0,
					length: 1,
					approved: sumBy(v.months, 'approved'),
					generic: sumBy(v.months, 'generic'),
					count: sumBy(v.months, 'count'),
					profit: sumBy(v.months, 'profit')
				}
			}));

			return {
				name: rank,
				id: rank,
				months: sumInfo(values.map((w) => w.months)),
				total: {
					amount: sumBy(rows, 'total.amount'),
					length: sumBy(rows, 'total.length'),
					approved: sumBy(rows, 'total.approved'),
					generic: sumBy(rows, 'total.generic'),
					count: sumBy(rows, 'total.count'),
					profit: sumBy(rows, 'total.profit')
				},
				rows
			};
		});

		return {
			name: manager,
			id: values[0].manager_id,
			months: sumInfo(values.map((w) => w.months)),
			total: {
				amount: sumBy(rows, 'total.amount'),
				length: sumBy(rows, 'total.length'),
				approved: sumBy(rows, 'total.approved'),
				generic: sumBy(rows, 'total.generic'),
				count: sumBy(rows, 'total.count'),
				profit: sumBy(rows, 'total.profit')
			},
			rows
		};
	});

	function sumInfo(array) {
		const months = [];

		for (let j = 0; j < array[0].length; j++) {
			months[j] = { amount: 0, length: 0, approved: 0, generic: 0, count: 0, profit: null };

			for (let i = 0; i < array.length; i++) {
				months[j] = {
					amount: months[j].amount + (array[i][j].valid > 0 ? 1 : 0),
					length: months[j].length + 1,
					approved: months[j].approved + array[i][j].approved,
					generic: months[j].generic + array[i][j].generic,
					count: months[j].count + array[i][j].count,
					profit: (months[j].profit || array[i][j].profit) && (months[j].profit + array[i][j].profit)
				};
			}
		}

		return months;
	}
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/managers-affiliates-activity/index.js